import { useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { LoadingOverlay } from '@mantine/core';
import { useAuthStore } from './stores/auth';
import { useImpersonationStore } from './stores/impersonation';
import { PERSONA_CONFIG } from './stores/persona';
import { signedInHomePath } from './pages/auth/magicLinkRedirect';
import { useFeatureFlagStore } from './stores/featureFlags';
import { IdleTimeoutProvider } from './components/auth';
import { UpdateBanner } from './components/common';
import { AdminLayout } from './components/layout/AdminLayout';
import { CoachLayout } from './components/layout/CoachLayout';
import { ParticipantLayout } from './components/layout/ParticipantLayout';
import { InSessionLayout } from './components/layout/InSessionLayout';
import { LoginPage } from './pages/auth/LoginPage';
import { CoachLoginPage } from './pages/auth/CoachLoginPage';
import { PartnerLoginPage } from './pages/auth/PartnerLoginPage';
import { ParticipantLoginPage } from './pages/auth/ParticipantLoginPage';
import { VerifyMagicLinkPage } from './pages/auth/VerifyMagicLinkPage';
import { AcceptInvitationPage } from './pages/auth/AcceptInvitationPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { PublicBookingPage } from './pages/public/PublicBookingPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { PartnersPage } from './pages/partners/PartnersPage';
import { PartnerDetailPage } from './pages/partners/PartnerDetailPage';
import { CohortsPage } from './pages/cohorts/CohortsPage';
import { CreateCohortPage } from './pages/cohorts/CreateCohortPage';
import { CohortDetailPage } from './pages/cohorts/CohortDetailPage';
import { SessionContentEditorPage } from './pages/cohorts/SessionContentEditorPage';
import { MatchingPage } from './pages/cohorts/matching/MatchingPage';
import { CoachesPage } from './pages/coaches/CoachesPage';
import { CoachDetailPage } from './pages/coaches/CoachDetailPage';
import {
  CoachDashboard,
  CoachProfilePage,
  CoachSessionsPage,
  CoachResourcesPage,
  CoachAvailabilityResponsePage,
  CoachCohortsPage,
  CoachCohortDetailPage,
  SessionLivePage,
  CoachCompensationPage,
} from './pages/coach';
import { CoachSessionPrepPage } from './pages/coach/CoachSessionPrepPage';
import { CoachActiveSessionPage } from './pages/coach/CoachActiveSessionPage';
import { CoachActiveSessionPreviewPage } from './pages/coach/CoachActiveSessionPreviewPage';
import { CoachPostSessionPage } from './pages/coach/CoachPostSessionPage';
import { ParticipantDashboard } from './pages/participant/ParticipantDashboard';
import { PreSessionReflectionPage } from './pages/participant/preSessionReflection/PreSessionReflectionPage';
import { PeerLeaderSessionPrepPage } from './pages/participant/peerLeaderPrep/PeerLeaderSessionPrepPage';
import { PeerLeaderPostSessionPage } from './pages/participant/peerLeaderPostSession/PeerLeaderPostSessionPage';
import { ParticipantPostSessionPage } from './pages/participant/postSession/ParticipantPostSessionPage';
import { SessionWaitingRoomPage } from './pages/participant/sessionWaitingRoom/SessionWaitingRoomPage';
import { ParticipantActiveSessionPage } from './pages/participant/sessionActive/ParticipantActiveSessionPage';
import { PartnerDashboard } from './pages/partner/PartnerDashboard';
import { NoAccessPage } from './pages/NoAccessPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { InSessionPreviewPage } from './pages/dev/InSessionPreviewPage';
import { AiDisclosurePreviewPage } from './pages/dev/AiDisclosurePreviewPage';
import { WaitingRoomPreviewPage } from './pages/dev/WaitingRoomPreviewPage';
import { ParticipantSessionPreviewPage } from './pages/dev/ParticipantSessionPreviewPage';
import { SessionPreviewDevPage } from './pages/dev/SessionPreviewDevPage';
import {
  CoachPostSessionPreview,
  ParticipantPostSessionPreview,
  PeerLeaderPostSessionPreview,
} from './pages/dev/PostSessionPreviewDevPages';
import { CoachPrepPreviewPage } from './pages/dev/CoachPrepPreviewPage';
import { PeerLeaderPrepPreviewPage } from './pages/dev/PeerLeaderPrepPreviewPage';
import { PreSessionReflectionPreviewPage } from './pages/dev/PreSessionReflectionPreviewPage';
import { AiInsightsPreviewPage } from './pages/dev/AiInsightsPreviewPage';
import { EditCohortSavePreviewPage } from './pages/dev/EditCohortSavePreviewPage';
import { GroupImportPreviewDevPage } from './pages/dev/GroupImportPreviewDevPage';
import { SessionHealthTooltipDevPage } from './pages/dev/SessionHealthTooltipDevPage';
import { MatchingDevPage } from './pages/dev/MatchingDevPage';
import { MatchingLiveDevPage } from './pages/dev/MatchingLiveDevPage';
import { TasksPage } from './pages/tasks';
import { TaskTemplatesPage } from './pages/admin/task-templates';
import { UserManagementPage } from './pages/admin/user-management';
import { EvergreenResourcesPage } from './pages/admin/evergreen-resources';
import { FrameworksPage } from './pages/admin/frameworks';
import { ExportDownloadPage } from './pages/exports';
import { DataLibraryPage } from './pages/data-library';
import { CoachDashboardPage } from './pages/coach-dashboard';
import { AbsenceEmailSettingsPage } from './pages/admin/absence-email-settings';
import { ImpersonationLogPage } from './pages/admin/impersonation-log/ImpersonationLogPage';
import { CompensationSettingsPage } from './pages/admin/compensation-settings/CompensationSettingsPage';
import { clearLoginReturn, readLoginReturn } from './utils/loginReturn';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, accessToken, _hasHydrated } = useAuthStore();
  const location = useLocation();
  // MED-1348: the saved return page is used once the user is signed in.
  const signedIn = isAuthenticated && !!accessToken;
  useEffect(() => {
    if (signedIn) clearLoginReturn();
  }, [signedIn]);

  if (!_hasHydrated) {
    return <LoadingOverlay visible />;
  }

  // Check both isAuthenticated AND that we have a token
  // Prevents stale isAuthenticated state from letting users through
  if (!isAuthenticated || !accessToken) {
    // Route persona paths to their login pages, everyone else to admin login
    let loginPath = '/login';
    if (location.pathname.startsWith('/coach')) loginPath = '/coach/login';
    else if (location.pathname.startsWith('/partner'))
      loginPath = '/partner/login';
    else if (location.pathname.startsWith('/participant'))
      loginPath = '/participant/login';
    // Save the attempted URL so we can redirect back after login
    return (
      <Navigate
        to={loginPath}
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  return <>{children}</>;
}

/**
 * MED-1389: the home of the role an admin chose to impersonate, or null. The
 * role label is the reliable signal; activePersona can stay 'admin' while
 * impersonating.
 */
function useImpersonatedRoleHome(): string | null {
  const role = useImpersonationStore(s =>
    s.isImpersonating ? s.impersonatedUserRole : null
  );
  return (
    Object.values(PERSONA_CONFIG).find(c => c.label === role)?.defaultRoute ??
    null
  );
}

interface PublicRouteProps {
  children: React.ReactNode;
}

function PublicRoute({ children }: PublicRouteProps) {
  const { isAuthenticated, accessToken, _hasHydrated, user } = useAuthStore();
  const impersonatedRoleHome = useImpersonatedRoleHome();
  const location = useLocation();

  if (!_hasHydrated) {
    return <LoadingOverlay visible />;
  }

  // Only redirect if both isAuthenticated AND we have a valid token.
  // This prevents a redirect loop when localStorage has stale isAuthenticated=true
  // but the token has already been cleared or is invalid.
  if (isAuthenticated && accessToken) {
    // Redirect to the page they were trying to access, or to their own home
    // (MED-1389: a coach on /coach/login went to the staff dashboard).
    // Guard against login paths in `from` to prevent circular redirect loops.
    const LOGIN_PATHS = [
      '/login',
      '/coach/login',
      '/partner/login',
      '/participant/login',
    ];
    const from = (location.state as { from?: string })?.from ?? readLoginReturn(user?.id);
    const safeTo =
      from && !LOGIN_PATHS.includes(from)
        ? from
        : signedInHomePath(user?.personas, impersonatedRoleHome);
    return <Navigate to={safeTo} replace />;
  }

  return <>{children}</>;
}

/**
 * MED-814 / E4.2 — Redirect `/sessions/:sessionId/prep` (MED-905 magic-link
 * contract) to the canonical participant-prefixed route. Wrapped in
 * ProtectedRoute so an unauthenticated visitor lands on the participant
 * login first (the `from` state preserves the original URL).
 */
function PeerLeaderPrepRedirect() {
  const { sessionId } = useParams<{ sessionId: string }>();
  return (
    <ProtectedRoute>
      <Navigate to={`/participant/sessions/${sessionId}/prep`} replace />
    </ProtectedRoute>
  );
}

/**
 * MED-1389: `/` is the staff dashboard. Many screens send a signed-in user to
 * `/` ("Go to Dashboard", accept invitation, a login with no return path), so
 * anyone else goes on to their own home here.
 */
function HomeRoute() {
  const personas = useAuthStore(s => s.user?.personas);
  const impersonatedRoleHome = useImpersonatedRoleHome();
  const home = signedInHomePath(personas, impersonatedRoleHome);
  return home === '/' ? <DashboardPage /> : <Navigate to={home} replace />;
}

// Placeholder component for routes not yet implemented
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div style={{ padding: '2rem' }}>
      <h1>{title}</h1>
      <p style={{ color: '#666' }}>This page is coming soon.</p>
    </div>
  );
}

function App() {
  const { _hasRehydrated, initializeAuth } = useAuthStore();
  const fetchFlags = useFeatureFlagStore(s => s.fetchFlags);

  // Validate auth state after Zustand rehydrates from localStorage.
  // _hasRehydrated means localStorage was read; initializeAuth then validates
  // the token and sets _hasHydrated when done (which gates route rendering).
  useEffect(() => {
    if (_hasRehydrated) {
      initializeAuth();
    }
  }, [_hasRehydrated, initializeAuth]);

  // Fetch feature flags on app start (no auth required)
  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  return (
    <BrowserRouter>
      <IdleTimeoutProvider>
        <UpdateBanner />
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          {/* Coach login with magic link */}
          <Route
            path="/coach/login"
            element={
              <PublicRoute>
                <CoachLoginPage />
              </PublicRoute>
            }
          />

          {/* Partner login with magic link */}
          <Route
            path="/partner/login"
            element={
              <PublicRoute>
                <PartnerLoginPage />
              </PublicRoute>
            }
          />

          {/* Participant login with magic link */}
          <Route
            path="/participant/login"
            element={
              <PublicRoute>
                <ParticipantLoginPage />
              </PublicRoute>
            }
          />

          {/* Magic link verification - standalone route (handles its own auth) */}
          <Route path="/auth/verify" element={<VerifyMagicLinkPage />} />

          {/* Accept invitation - standalone route (no auth redirect) */}
          <Route
            path="/accept-invitation/:token"
            element={<AcceptInvitationPage />}
          />

          {/* Password reset routes - public, no auth redirect */}
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* MED-1319: Logged-out 1:1 booking from a coach's share link.
            Not wrapped in PublicRoute or ProtectedRoute, so a visitor who is
            logged in as any persona can also open it. */}
          <Route path="/book/:token" element={<PublicBookingPage />} />

          {/* MED-814 / E4.2 — Magic-link alias from MED-905 emails. The peer
            leader Prep Unlock + RSVP emails route to `/sessions/:id/prep`
            (Spec Decision #3 in MED-905). After auth, redirect to the
            canonical participant-prefixed route. */}
          <Route
            path="/sessions/:sessionId/prep"
            element={<PeerLeaderPrepRedirect />}
          />

          {/* MED-805 / E3.3: Participant Active Session — immersive full-screen,
            rendered OUTSIDE ParticipantLayout so the app chrome stays out of
            the way per AC #2 (focused dark layout to minimize distractions). */}
          <Route
            path="/participant/sessions/:sessionId/active"
            element={
              <ProtectedRoute>
                <ParticipantActiveSessionPage />
              </ProtectedRoute>
            }
          />

          {/* MED-1048 / E3.2b — Session Waiting Room. Moved OUT of the light
            ParticipantLayout to the immersive dark shell so the waiting room is
            the seamless "front porch" of the active session (which is also
            rendered outside the layout, above). */}
          <Route
            path="/participant/sessions/:sessionId/waiting-room"
            element={
              <ProtectedRoute>
                <SessionWaitingRoomPage />
              </ProtectedRoute>
            }
          />

          {/* MED-1010 — Immersive in-session shell shared by coach (MED-792) and
            peer-leader (MED-816) active sessions. Both render the same
            CoachActiveSessionPage; the page detects the `/participant/` prefix
            to reroute Back / End Session / AI-dismiss. Pulled out of
            CoachLayout/ParticipantLayout so the in-session top bar offers no
            navigation away from an active session. These specific paths
            out-rank the `/coach/*` and `/participant/*` catch-alls below. */}
          <Route
            element={
              <ProtectedRoute>
                <InSessionLayout />
              </ProtectedRoute>
            }
          >
            <Route
              path="/coach/cohorts/:cohortId/sessions/:sessionId/active"
              element={<CoachActiveSessionPage />}
            />
            <Route
              path="/participant/cohorts/:cohortId/sessions/:sessionId/active"
              element={<CoachActiveSessionPage />}
            />
          </Route>

          {/* MED-1068 / E* — Coach Active Session Preview (read-only). Pulled out
            of CoachLayout/ParticipantLayout so the full global app nav is no
            longer the outer chrome: the page renders the black Session Preview
            bar topmost with the reduced in-session header beneath it (AC1). The
            same page serves all three entry points — coach (from prep), peer
            leader (`/participant/...`), and CM (`?from=session-content`); it
            detects context to route Close Preview correctly. These specific
            paths out-rank the `/coach/*` and `/participant/*` catch-alls below. */}
          <Route
            path="/coach/cohorts/:cohortId/sessions/:sessionId/preview"
            element={
              <ProtectedRoute>
                <CoachActiveSessionPreviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/participant/cohorts/:cohortId/sessions/:sessionId/preview"
            element={
              <ProtectedRoute>
                <CoachActiveSessionPreviewPage />
              </ProtectedRoute>
            }
          />

          {/* MED-1010 — Dev-only static preview of the immersive in-session
            shell, so the chrome can be inspected without a live `active`
            session. Compiled out of production builds. No ProtectedRoute so it
            renders in any auth state; coach vs. peer-leader variant is selected
            by path. Visit /dev/in-session or /dev/in-session/peer. */}
          {/* MED-1269 — Dev-only harness for the Edit Cohort Details deferred-save
            work. Renders the real modal with a mocked API + on-screen call log,
            so the require-Save behavior can be verified without auth or a DB.
            Compiled out of production builds. Visit /dev/edit-cohort-save. */}
          {import.meta.env.DEV && (
            <Route
              path="/dev/edit-cohort-save"
              element={<EditCohortSavePreviewPage />}
            />
          )}
          {/* MED-1290 — Dev-only harness for the group-import preview messaging.
            Renders the real upload modal with the preview API stubbed, so the
            unreadable/mixed/clean states can be inspected without auth or a DB.
            Compiled out of production builds. Visit /dev/group-import. */}
          {import.meta.env.DEV && (
            <Route
              path="/dev/group-import"
              element={<GroupImportPreviewDevPage />}
            />
          )}
          {/* Dev-only harness for the in-app matching tool. Renders the real
            setup flow and progress screen against an in-memory matching-server
            seeded with the GS26 sample. Visit /dev/matching. */}
          {import.meta.env.DEV && (
            <Route path="/dev/matching" element={<MatchingDevPage />} />
          )}
          {/* Same matching tool against a local matching-server (default
            localhost:4000). Session calls go through the dev plugin in
            scripts/matchingDevServer.ts. Visit /dev/matching/live. */}
          {import.meta.env.DEV && (
            <Route path="/dev/matching/live" element={<MatchingLiveDevPage />} />
          )}
          {/* MED-1288 — Dev-only harness for the Session Health flag tooltip.
            Renders the real drill-down against a fixture matching the reported
            case, so the reason lines can be checked without the attendance data
            needed to trip the rules. Compiled out of production builds.
            Visit /dev/session-health-tooltip. */}
          {import.meta.env.DEV && (
            <Route
              path="/dev/session-health-tooltip"
              element={<SessionHealthTooltipDevPage />}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              element={
                <InSessionLayout forceImmersive contextOverride="coach" />
              }
            >
              <Route
                path="/dev/in-session"
                element={<InSessionPreviewPage />}
              />
            </Route>
          )}
          {import.meta.env.DEV && (
            <Route
              element={
                <InSessionLayout forceImmersive contextOverride="participant" />
              }
            >
              <Route
                path="/dev/in-session/peer"
                element={<InSessionPreviewPage />}
              />
            </Route>
          )}

          {/* MED-1030 — Dev-only static preview of the participant in-session
            screen. Renders the real ParticipantActiveSessionPage (its own
            immersive shell — no layout wrapper) from an in-memory fixture.
            Compiled out of production builds. Visit /dev/participant-session
            or /dev/participant-session/peer. */}
          {import.meta.env.DEV && (
            <Route
              path="/dev/participant-session"
              element={<ParticipantSessionPreviewPage />}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/dev/participant-session/peer"
              element={<ParticipantSessionPreviewPage />}
            />
          )}

          {/* MED-1167 — Dev-only static preview of the AI Insights panel with
            the New/Held triage. Renders the real ActiveSessionInsightsPanel over
            an in-memory nudge fixture (no session-intelligence socket). Compiled
            out of production builds. Visit /dev/ai-insights. */}
          {import.meta.env.DEV && (
            <Route
              path="/dev/ai-insights"
              element={<AiInsightsPreviewPage />}
            />
          )}

          {/* MED-1047 — Dev-only static preview of the AI disclosure modal, so
            the finalized copy + visual can be reviewed without driving into a
            live session. Compiled out of production builds. Visit
            /dev/ai-disclosure (participant) or /dev/ai-disclosure/leader. */}
          {import.meta.env.DEV && (
            <Route
              path="/dev/ai-disclosure"
              element={<AiDisclosurePreviewPage />}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/dev/ai-disclosure/leader"
              element={<AiDisclosurePreviewPage />}
            />
          )}

          {/* MED-1048 — Dev-only static preview of the participant waiting room
            across its three states (normal / no-show / missed). Compiled out of
            production builds. Visit /dev/waiting-room, /dev/waiting-room/no-show,
            or /dev/waiting-room/missed. */}
          {import.meta.env.DEV && (
            <Route
              path="/dev/waiting-room"
              element={<WaitingRoomPreviewPage />}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/dev/waiting-room/no-show"
              element={<WaitingRoomPreviewPage />}
            />
          )}
          {/* MED-1244 — Phase 1 (confirmed-leader head start) preview. */}
          {import.meta.env.DEV && (
            <Route
              path="/dev/waiting-room/no-show-headstart"
              element={<WaitingRoomPreviewPage />}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/dev/waiting-room/missed"
              element={<WaitingRoomPreviewPage />}
            />
          )}

          {/* MED-1068 — Dev-only static preview of the polished Session Preview
            chrome across its three entry roles, from an in-memory fixture (no
            live session / preview-state API). Compiled out of production builds.
            Visit /dev/session-preview, /dev/session-preview/peer, or
            /dev/session-preview/cm. */}
          {import.meta.env.DEV && (
            <Route
              path="/dev/session-preview"
              element={<SessionPreviewDevPage />}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/dev/session-preview/peer"
              element={<SessionPreviewDevPage />}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/dev/session-preview/cm"
              element={<SessionPreviewDevPage />}
            />
          )}

          {/* MED-1040 — Dev-only FULL post-session previews: the real pages
            rendered from in-memory fixtures inside their role layout (coach
            navy / participant + peer-leader green), so the full app context
            (top nav + header + cards) shows like /dev/in-session. Compiled out
            of production builds. */}
          {import.meta.env.DEV && (
            <Route element={<CoachLayout />}>
              <Route
                path="/dev/post-session/coach"
                element={<CoachPostSessionPreview />}
              />
            </Route>
          )}
          {import.meta.env.DEV && (
            <Route element={<ParticipantLayout />}>
              <Route
                path="/dev/post-session/participant"
                element={<ParticipantPostSessionPreview />}
              />
              <Route
                path="/dev/post-session/peer"
                element={<PeerLeaderPostSessionPreview />}
              />
            </Route>
          )}

          {/* MED-1089 — Dev-only Coach Session Prep preview: the real page from
            an in-memory fixture inside CoachLayout (navy chrome). Compiled out
            of production builds. Visit /dev/session-prep/coach. */}
          {import.meta.env.DEV && (
            <Route element={<CoachLayout />}>
              <Route
                path="/dev/session-prep/coach"
                element={<CoachPrepPreviewPage />}
              />
            </Route>
          )}

          {/* MED-1092 — Dev-only Peer-Leader Session Prep preview: the real page
            from in-memory fixtures inside ParticipantLayout. Compiled out of
            production builds. Visit /dev/session-prep/peer. */}
          {import.meta.env.DEV && (
            <Route element={<ParticipantLayout />}>
              <Route
                path="/dev/session-prep/peer"
                element={<PeerLeaderPrepPreviewPage />}
              />
            </Route>
          )}

          {/* MED-1090 — Dev-only Participant Pre-Session Reflection preview: the
            real page from an in-memory fixture inside ParticipantLayout.
            Compiled out of production builds. Visit /dev/session-prep/participant. */}
          {import.meta.env.DEV && (
            <Route element={<ParticipantLayout />}>
              <Route
                path="/dev/session-prep/participant"
                element={<PreSessionReflectionPreviewPage />}
              />
            </Route>
          )}

          {/* Protected participant routes with participant layout */}
          <Route
            path="/participant/*"
            element={
              <ProtectedRoute>
                <ParticipantLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ParticipantDashboard />} />
            {/* MED-803 / E3.1: Pre-Session Reflection page (magic-link aware) */}
            <Route
              path="sessions/:sessionId/reflection"
              element={<PreSessionReflectionPage />}
            />
            {/* MED-806 / E3.4: Post-Session — Participant View */}
            <Route
              path="sessions/:sessionId/post-session"
              element={<ParticipantPostSessionPage />}
            />
            {/* MED-814 / E4.2: Peer Leader Pre-Session Prep page */}
            <Route
              path="sessions/:sessionId/prep"
              element={<PeerLeaderSessionPrepPage />}
            />
            {/* MED-909 / E4.6 + MED-817: Peer Leader Post-Session page */}
            <Route
              path="cohorts/:cohortId/sessions/:sessionId/post-session"
              element={<PeerLeaderPostSessionPage />}
            />
          </Route>

          {/* Protected coach routes with coach layout */}
          <Route
            path="/coach/*"
            element={
              <ProtectedRoute>
                <CoachLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<CoachDashboard />} />
            <Route path="cohorts" element={<CoachCohortsPage />} />
            <Route
              path="cohorts/:cohortId"
              element={<CoachCohortDetailPage />}
            />
            {/* MED-793: Coach Session Prep flow */}
            <Route
              path="cohorts/:cohortId/sessions/:sessionId/prep"
              element={<CoachSessionPrepPage />}
            />
            {/* MED-794: Coach Post-Session screen */}
            <Route
              path="cohorts/:cohortId/sessions/:sessionId/post-session"
              element={<CoachPostSessionPage />}
            />
            <Route path="compensation" element={<CoachCompensationPage />} />
            <Route path="sessions" element={<CoachSessionsPage />} />
            <Route path="resources" element={<CoachResourcesPage />} />
            <Route path="profile" element={<CoachProfilePage />} />
            <Route
              path="availability/:requestId"
              element={<CoachAvailabilityResponsePage />}
            />
            <Route path="session/live" element={<SessionLivePage />} />
          </Route>

          {/* Protected routes with admin layout */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            {/* Admin Dashboard (default) */}
            <Route index element={<HomeRoute />} />

            {/* Admin/Cohort Manager routes (both personas share main dashboard) */}
            <Route path="cohorts" element={<CohortsPage />} />
            <Route path="cohorts/new" element={<CreateCohortPage />} />
            <Route path="cohorts/:id" element={<CohortDetailPage />} />
            {/* In-app matching tool: setup flow and progress for one matching session */}
            <Route
              path="cohorts/:cohortId/matching/:sessionId"
              element={<MatchingPage />}
            />
            {/* MED-784: CM Session Content Editor (full-page, AC13 entry from SessionDetailDrawer) */}
            <Route
              path="cohorts/:cohortId/sessions/:sessionId/content"
              element={<SessionContentEditorPage />}
            />
            {/* MED-1086 (E1.3m): session-number-addressed editor — the "Build
                Content" entry point reachable before groups exist. Same page,
                addressed by session number instead of a session row. */}
            <Route
              path="cohorts/:cohortId/sessions/by-number/:sessionNumber/content"
              element={<SessionContentEditorPage />}
            />
            <Route
              path="sessions"
              element={<PlaceholderPage title="Sessions" />}
            />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="data-library" element={<DataLibraryPage />} />
            <Route path="coach-dashboard" element={<CoachDashboardPage />} />
            <Route path="coaches" element={<CoachesPage />} />
            <Route path="coaches/:id" element={<CoachDetailPage />} />
            <Route path="partners" element={<PartnersPage />} />
            <Route path="partners/:id" element={<PartnerDetailPage />} />
            <Route path="users" element={<UserManagementPage />} />
            <Route
              path="admin/task-templates"
              element={<TaskTemplatesPage />}
            />
            <Route
              path="admin/evergreen-resources"
              element={<EvergreenResourcesPage />}
            />
            <Route path="admin/frameworks" element={<FrameworksPage />} />
            <Route
              path="admin/absence-email-settings"
              element={<AbsenceEmailSettingsPage />}
            />
            <Route
              path="admin/impersonation-log"
              element={<ImpersonationLogPage />}
            />
            <Route
              path="admin/compensation-settings"
              element={<CompensationSettingsPage />}
            />

            {/* Partner routes */}
            <Route path="partner" element={<PartnerDashboard />} />
            <Route
              path="partner/cohorts"
              element={<PlaceholderPage title="Cohorts" />}
            />
            <Route
              path="partner/reports"
              element={<PlaceholderPage title="Reports" />}
            />

            {/* Export download page */}
            <Route path="exports" element={<ExportDownloadPage />} />

            {/* No access page */}
            <Route path="no-access" element={<NoAccessPage />} />

            {/* Catch all - show 404 for unknown routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </IdleTimeoutProvider>
    </BrowserRouter>
  );
}

export default App;
